import { type FormEvent, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useContact } from '../../hooks/useContact'
import type { ContactPayload } from '../../types/contact'
import styles from './ContactPage.module.css'

/* ─────────────────────────────────────────────────
   Icônes SVG inline
───────────────────────────────────────────────── */

const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.btnIcon}>
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.feedbackIcon}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

const IconAlertCircle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.feedbackIcon}>
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
)

const IconLoader = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" aria-hidden="true" className={styles.spinner}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

const IconGitHub = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={styles.socialIcon}>
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
)

const IconXTwitter = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={styles.socialIcon}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

const IconLinkedIn = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={styles.socialIcon}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

const CONTACT_SOCIALS = [
  { href: 'https://github.com/StevenYAMBOS', label: 'GitHub', icon: <IconGitHub /> },
  { href: 'https://x.com/StevenYambos', label: 'X (Twitter)', icon: <IconXTwitter /> },
  { href: 'https://www.linkedin.com/in/steven-yambos', label: 'LinkedIn', icon: <IconLinkedIn /> },
]

const IconPaperclip = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.fieldIcon}>
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
)

const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.removeIcon}>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

/* ─────────────────────────────────────────────────
   Constantes
───────────────────────────────────────────────── */

/** Extensions acceptées par l'API (voir ContactService.cs) */
const ALLOWED_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.webp', '.svg']
const MAX_FILE_SIZE_MB   = 5

/* ─────────────────────────────────────────────────
   Validation locale
───────────────────────────────────────────────── */

interface FormErrors {
  email?:   string
  subject?: string
  content?: string
  file?:    string
}

function validate(payload: ContactPayload): FormErrors {
  const errors: FormErrors = {}

  if (!payload.email.trim()) {
    errors.email = "L'adresse email est requise."
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    errors.email = "L'adresse email n'est pas valide."
  } else if (payload.email.length > 100) {
    errors.email = "L'adresse email ne doit pas dépasser 100 caractères."
  }

  if (!payload.subject.trim()) {
    errors.subject = 'Le sujet est requis.'
  } else if (payload.subject.length > 200) {
    errors.subject = 'Le sujet ne doit pas dépasser 200 caractères.'
  }

  if (!payload.content.trim()) {
    errors.content = 'Le message est requis.'
  } else if (payload.content.trim().length < 10) {
    errors.content = 'Le message doit contenir au moins 10 caractères.'
  } else if (payload.content.length > 1000) {
    errors.content = 'Le message ne doit pas dépasser 1 000 caractères.'
  }

  if (payload.file) {
    /* Vérifie l'extension */
    const ext = '.' + payload.file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      errors.file = `Format non supporté. Extensions acceptées : ${ALLOWED_EXTENSIONS.join(', ')}`
    }
    /* Vérifie la taille */
    else if (payload.file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      errors.file = `Le fichier ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo.`
    }
  }

  return errors
}

/* ─────────────────────────────────────────────────
   Composant
───────────────────────────────────────────────── */

export function ContactPage() {
  const { status, errorMessage, submit, reset } = useContact()

  const [fields, setFields] = useState({
    email:   '',
    subject: '',
    content: '',
  })

  /* Le fichier est géré séparément — c'est un objet File, pas une string */
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errors, setErrors]             = useState<FormErrors>({})

  const contentLength = fields.content.length

  /* Refs pour focus sur le 1er champ en erreur */
  const emailRef   = useRef<HTMLInputElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLTextAreaElement>(null)
  const fileRef    = useRef<HTMLInputElement>(null)

  const isLoading = status === 'loading'
  const isSuccess = status === 'success'

  /* ── Handlers champs texte ── */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFields((prev) => ({ ...prev, [name]: value }))
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  /* ── Handler fichier ── */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    setSelectedFile(file)
    /* Efface l'erreur fichier dès qu'un nouveau fichier est choisi */
    if (errors.file) setErrors((prev) => ({ ...prev, file: undefined }))
  }

  /* Supprime le fichier sélectionné */
  const handleRemoveFile = () => {
    setSelectedFile(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  /* ── Submit ── */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const payload: ContactPayload = {
      ...fields,
      file: selectedFile ?? undefined,
    }

    const validationErrors = validate(payload)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      if (validationErrors.email)        emailRef.current?.focus()
      else if (validationErrors.subject) subjectRef.current?.focus()
      else if (validationErrors.content) contentRef.current?.focus()
      else if (validationErrors.file)    fileRef.current?.focus()
      return
    }

    await submit(payload)
  }

  /* ── Reset ── */
  const handleReset = () => {
    setFields({ email: '', subject: '', content: '' })
    setSelectedFile(null)
    setErrors({})
    reset()
  }

  /* ── Vue succès ── */
  if (isSuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.successCard} role="alert" aria-live="polite">
          <div className={styles.successIconRing}><IconCheck /></div>
          <h2 className={styles.successTitle}>Message envoyé !</h2>
          <p className={styles.successText}>
            Merci pour votre message. Je vous répondrai dans les plus brefs délais.
          </p>
          <button type="button" onClick={handleReset} className={styles.ghostBtn}>
            Envoyer un autre message
          </button>
        </div>
      </div>
    )
  }

  /* ── Vue formulaire ── */
  return (
    <div className={styles.page}>
      <Helmet>
        <title>Steven YAMBOS — Contact</title>
        <meta name="description" content="Contactez Steven YAMBOS, Développeur Back-End C# .NET basé à Paris. Une question, une opportunité ou un projet ? Écrivez-moi directement." />
        <meta property="og:title" content="Steven YAMBOS — Contact" />
        <meta property="og:description" content="Contactez Steven YAMBOS, Développeur Back-End C# .NET basé à Paris." />
        <meta property="og:url" content="https://stevenyambos.fr/contact" />
      </Helmet>
      <div className={styles.bgGrid} aria-hidden="true" />
      <div className={styles.bgOrb}  aria-hidden="true" />

      {/* ── En-tête ── */}
      <div className={styles.header}>
        <h1 className={styles.title}>Contact</h1>
        <div className={styles.titleBar} aria-hidden="true" />
        <p className={styles.subtitle}>
          Une question, une opportunité ou juste envie de discuter ?<br />
          Écrivez-moi directement.
        </p>
      </div>

      {/* ── Réseaux sociaux ── */}
      <div className={styles.contactSocials}>
        {CONTACT_SOCIALS.map(({ href, label, icon }) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className={styles.contactSocialLink}
          >
            {icon}
            <span className={styles.socialName}>{label}</span>
          </a>
        ))}
      </div>

      {/* ── Card formulaire ── */}
      <div className={styles.card}>
        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label="Formulaire de contact"
          /* Pas de encType nécessaire — FormData le gère nativement */
        >

          {/* Email */}
          <div className={`${styles.field} ${errors.email ? styles.fieldError : ''}`}>
            <label htmlFor="contact-email" className={styles.label}>
              Email <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <input
              ref={emailRef}
              id="contact-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="email@exemple.com"
              className={styles.input}
              value={fields.email}
              onChange={handleChange}
              maxLength={100}
              aria-required="true"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'error-email' : undefined}
              disabled={isLoading}
            />
            {errors.email && (
              <span id="error-email" className={styles.errorMsg} role="alert">{errors.email}</span>
            )}
          </div>

          {/* Sujet */}
          <div className={`${styles.field} ${errors.subject ? styles.fieldError : ''}`}>
            <label htmlFor="contact-subject" className={styles.label}>
              Sujet <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <input
              ref={subjectRef}
              id="contact-subject"
              name="subject"
              type="text"
              placeholder="Ex : Opportunité de mission, Question technique…"
              className={styles.input}
              value={fields.subject}
              onChange={handleChange}
              maxLength={200}
              aria-required="true"
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? 'error-subject' : undefined}
              disabled={isLoading}
            />
            {errors.subject && (
              <span id="error-subject" className={styles.errorMsg} role="alert">{errors.subject}</span>
            )}
          </div>

          {/* Contenu */}
          <div className={`${styles.field} ${errors.content ? styles.fieldError : ''}`}>
            <div className={styles.labelRow}>
              <label htmlFor="contact-content" className={styles.label}>
                Message <span className={styles.required} aria-hidden="true">*</span>
              </label>
              <span
                className={`${styles.counter} ${contentLength > 900 ? styles.counterWarn : ''}`}
                aria-live="polite"
                aria-label={`${contentLength} caractères sur 1000`}
              >
                {contentLength} / 1000
              </span>
            </div>
            <textarea
              ref={contentRef}
              id="contact-content"
              name="content"
              rows={6}
              placeholder="Bonjour Steven, je souhaitais vous contacter au sujet de…"
              className={styles.textarea}
              value={fields.content}
              onChange={handleChange}
              maxLength={1000}
              aria-required="true"
              aria-invalid={!!errors.content}
              aria-describedby={errors.content ? 'error-content' : undefined}
              disabled={isLoading}
            />
            {errors.content && (
              <span id="error-content" className={styles.errorMsg} role="alert">{errors.content}</span>
            )}
          </div>

          {/* Fichier joint — input type="file" natif */}
          <div className={`${styles.field} ${errors.file ? styles.fieldError : ''}`}>
            <label htmlFor="contact-file" className={styles.label}>
              <IconPaperclip />
              Fichier joint
              <span className={styles.optional}>(optionnel)</span>
            </label>

            {/* Zone de sélection custom */}
            <div className={styles.fileWrapper}>
              <input
                ref={fileRef}
                id="contact-file"
                name="file"
                type="file"
                accept={ALLOWED_EXTENSIONS.join(',')}
                className={styles.fileInput}
                onChange={handleFileChange}
                aria-describedby={errors.file ? 'error-file' : 'hint-file'}
                disabled={isLoading}
              />
              {/* Label visuel cliquable */}
              <label htmlFor="contact-file" className={styles.fileLabel} aria-hidden="true">
                {selectedFile ? (
                  /* Fichier sélectionné : affiche le nom */
                  <span className={styles.fileName}>{selectedFile.name}</span>
                ) : (
                  <span className={styles.filePlaceholder}>
                    Choisir un fichier…
                  </span>
                )}
              </label>

              {/* Bouton supprimer si un fichier est sélectionné */}
              {selectedFile && (
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className={styles.fileRemove}
                  aria-label="Supprimer le fichier joint"
                >
                  <IconX />
                </button>
              )}
            </div>

            <span id="hint-file" className={styles.hint}>
              Formats acceptés : {ALLOWED_EXTENSIONS.join(', ')} — max. {MAX_FILE_SIZE_MB} Mo
            </span>
            {errors.file && (
              <span id="error-file" className={styles.errorMsg} role="alert">{errors.file}</span>
            )}
          </div>

          {/* Erreur API globale */}
          {status === 'error' && errorMessage && (
            <div className={styles.apiError} role="alert" aria-live="assertive">
              <IconAlertCircle /><span>{errorMessage}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading
              ? <><IconLoader />Envoi en cours…</>
              : <><IconSend />Envoyer le message</>
            }
          </button>

        </form>
      </div>
    </div>
  )
}